import { useState, useRef, useCallback, useEffect } from "react";
import { Video, X, Check, RotateCcw, Camera, Square } from "lucide-react";

interface CameraVideoRecorderProps {
  isOpen: boolean;
  onClose: () => void;
  onVideoRecorded: (file: File, duration: number) => void;
  minDuration?: number;
  maxDuration?: number;
}

const CameraVideoRecorder = ({
  isOpen,
  onClose,
  onVideoRecorded,
  minDuration = 5,
  maxDuration = 15,
}: CameraVideoRecorderProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");

  // Initialize camera
  const initCamera = useCallback(async () => {
    try {
      setError(null);
      setCameraReady(false);

      // Stop existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1080 },
          height: { ideal: 1920 },
        },
        audio: true,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraReady(true);
      }
    } catch (err: any) {
      console.error("Camera error:", err);
      if (err.name === "NotAllowedError") {
        setError("Camera permission denied. Please allow camera access.");
      } else if (err.name === "NotFoundError") {
        setError("No camera found on this device.");
      } else {
        setError("Failed to access camera. Please try again.");
      }
    }
  }, [facingMode]);

  // Start recording
  const startRecording = useCallback(() => {
    if (!streamRef.current) return;

    chunksRef.current = [];
    setDuration(0);
    setRecordedBlob(null);
    setRecordedUrl(null);

    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
      ? "video/webm;codecs=vp9"
      : MediaRecorder.isTypeSupported("video/webm")
      ? "video/webm"
      : "video/mp4";

    const mediaRecorder = new MediaRecorder(streamRef.current, {
      mimeType,
      videoBitsPerSecond: 2500000,
    });

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      setRecordedBlob(blob);
      const url = URL.createObjectURL(blob);
      setRecordedUrl(url);
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start(100);
    setIsRecording(true);

    // Start timer
    let elapsed = 0;
    timerRef.current = setInterval(() => {
      elapsed += 0.1;
      setDuration(elapsed);

      // Auto-stop at max duration
      if (elapsed >= maxDuration) {
        stopRecording();
      }
    }, 100);
  }, [maxDuration]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isRecording]);

  // Re-record
  const reRecord = useCallback(() => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setDuration(0);
  }, [recordedUrl]);

  // Confirm recording
  const confirmRecording = useCallback(() => {
    if (!recordedBlob || duration < minDuration) return;

    const file = new File(
      [recordedBlob],
      `intro-video-${Date.now()}.webm`,
      { type: recordedBlob.type }
    );

    onVideoRecorded(file, duration);
    onClose();
  }, [recordedBlob, duration, minDuration, onVideoRecorded, onClose]);

  // Switch camera
  const switchCamera = useCallback(() => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  }, []);

  // Initialize camera when modal opens
  useEffect(() => {
    if (isOpen) {
      initCamera();
    }

    return () => {
      // Cleanup
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
    };
  }, [isOpen, initCamera]);

  // Handle close
  const handleClose = useCallback(() => {
    if (isRecording) {
      stopRecording();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setDuration(0);
    setCameraReady(false);
    onClose();
  }, [isRecording, stopRecording, recordedUrl, onClose]);

  if (!isOpen) return null;

  const canConfirm = recordedBlob && duration >= minDuration;
  const durationPercent = Math.min((duration / maxDuration) * 100, 100);
  const isUnderMin = duration < minDuration && duration > 0;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/60 to-transparent">
        <button
          onClick={handleClose}
          className="p-2 rounded-full bg-black/40 backdrop-blur-sm"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        <div className="text-white text-center">
          <p className="text-sm font-medium">Record Intro Video</p>
          <p className="text-xs text-white/70">{minDuration}-{maxDuration} seconds</p>
        </div>

        {!recordedUrl && (
          <button
            onClick={switchCamera}
            className="p-2 rounded-full bg-black/40 backdrop-blur-sm"
          >
            <RotateCcw className="w-6 h-6 text-white" />
          </button>
        )}
        {recordedUrl && <div className="w-10" />}
      </div>

      {/* Video Preview */}
      <div className="flex-1 relative">
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center text-white text-center p-8">
            <div>
              <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">Camera Error</p>
              <p className="text-sm text-white/70">{error}</p>
              <button
                onClick={initCamera}
                className="mt-4 px-4 py-2 rounded-full bg-white/20 text-sm"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : recordedUrl ? (
          // Playback recorded video
          <video
            src={recordedUrl}
            className="w-full h-full object-cover"
            autoPlay
            loop
            playsInline
            muted
          />
        ) : (
          // Live camera feed
          <video
            ref={videoRef}
            className={`w-full h-full object-cover ${
              facingMode === "user" ? "scale-x-[-1]" : ""
            }`}
            autoPlay
            playsInline
            muted
          />
        )}

        {/* Recording indicator */}
        {isRecording && (
          <div className="absolute top-20 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full bg-destructive/90 backdrop-blur-sm">
            <div className="w-3 h-3 rounded-full bg-white animate-pulse" />
            <span className="text-white font-medium">{duration.toFixed(1)}s</span>
          </div>
        )}

        {/* Progress bar during recording */}
        {isRecording && (
          <div className="absolute bottom-32 left-4 right-4">
            <div className="h-2 bg-white/30 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-100 ${
                  isUnderMin ? "bg-yellow-500" : "bg-pop"
                }`}
                style={{ width: `${durationPercent}%` }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-white/70">
              <span>{minDuration}s min</span>
              <span>{maxDuration}s max</span>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/80 to-transparent">
        {recordedUrl ? (
          // Review controls
          <div className="flex items-center justify-center gap-8">
            <button
              onClick={reRecord}
              className="flex flex-col items-center gap-1"
            >
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                <RotateCcw className="w-6 h-6 text-white" />
              </div>
              <span className="text-xs text-white">Retake</span>
            </button>

            <button
              onClick={confirmRecording}
              disabled={!canConfirm}
              className="flex flex-col items-center gap-1"
            >
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  canConfirm ? "bg-pop" : "bg-white/20 opacity-50"
                }`}
              >
                <Check className="w-8 h-8 text-white" />
              </div>
              <span className="text-xs text-white">
                {canConfirm ? "Use Video" : `Need ${minDuration}s+`}
              </span>
            </button>
          </div>
        ) : (
          // Record controls
          <div className="flex items-center justify-center">
            {isRecording ? (
              <button
                onClick={stopRecording}
                disabled={duration < minDuration}
                className="flex flex-col items-center gap-2"
              >
                <div
                  className={`w-20 h-20 rounded-full border-4 border-white flex items-center justify-center ${
                    duration >= minDuration ? "bg-destructive" : "bg-white/20"
                  }`}
                >
                  <Square className="w-8 h-8 text-white fill-white" />
                </div>
                <span className="text-xs text-white">
                  {duration < minDuration
                    ? `Record ${(minDuration - duration).toFixed(1)}s more`
                    : "Stop Recording"}
                </span>
              </button>
            ) : (
              <button
                onClick={startRecording}
                disabled={!cameraReady}
                className="flex flex-col items-center gap-2"
              >
                <div className="w-20 h-20 rounded-full border-4 border-white bg-destructive flex items-center justify-center">
                  <div className="w-6 h-6 rounded-full bg-white" />
                </div>
                <span className="text-xs text-white">
                  {cameraReady ? "Start Recording" : "Loading camera..."}
                </span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CameraVideoRecorder;
